import { useRouter } from 'next/router'
import { Fragment, type FC } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Spinner } from '@wartek-id/spinner'
import { Button } from '@wartek-id/button-v1'
import { Separator } from '@wartek-id/separator'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'

import { Header } from 'app/KinerjaShared/components/Header'
import usePerencanaan from 'app/KinerjaShared/hooks/usePerencanaan'
import PanelKinerja from 'app/KinerjaKSPage/components/PanelKinerja'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import CardStateError from 'app/KinerjaShared/components/CardStateError'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import useToggleSdkContext from 'utils/useToogleSdk'
import { assetUrl } from 'utils/url'
import { PanelKinerjaSourceTabId, type School } from 'types/kinerja-ks'

interface PerencanaanProps {
  npsn?: School['npsn']
  ptkId?: string
}

const Perencanaan: FC<PerencanaanProps> = ({ npsn, ptkId }) => {
  const router = useRouter()

  const { periodYear } = usePeriodContext()
  const toggleValue = useToggleSdkContext()
  const isHideRhkPointEnabled =
    toggleValue?.['kinerja-hide-rhk-point-enabled'] || false
  const hideRhkDetails = isHideRhkPointEnabled && periodYear >= 2025

  const {
    isLoading: isLoadingPerencanaan,
    isFetched,
    namaKs,
    satuanPendidikan,
    content,
    perilakuKerja,
    refetch,
  } = usePerencanaan({ npsn, ptkId, role: 'DINAS' })

  const isLoading = !router.isReady || isLoadingPerencanaan

  const rencanaHasilKerja = content.filter(
    (c) => c.sourceTabId === PanelKinerjaSourceTabId.RencanaHasilKerjaEkstra
  )
  const praktikKinerja = content.filter(
    (c) => c.sourceTabId === PanelKinerjaSourceTabId.PraktikKinerjaKepalaSekolah
  )
  const pengembanganKompetensi = content.filter(
    (c) => c.sourceTabId === PanelKinerjaSourceTabId.PengembanganKompetensi
  )

  return (
    <div>
      <Header
        title="Rincian Perencanaan Kinerja"
        className="mb-9 print:hidden"
        onBack={() => {
          router.back()
        }}
      />

      {isLoading && (
        <div className="flex flex-col gap-4 justify-center items-center">
          <Spinner data-testid="loading-spinner" />
        </div>
      )}

      {!isLoading && !isFetched && (
        <CardStateError
          onClick={() => {
            refetch()
          }}
        />
      )}

      {!isLoading && isFetched && (
        <>
          <div className="bg-white rounded-lg mt-8">
            <div className="p-6 space-y-10">
              <div className="flex items-center gap-3">
                <Icon className="!text-4xl" fontSize="lg">
                  account_circle_outlined
                </Icon>

                {isLoading ? (
                  <>
                    <Shimmer height={32} width="100%" />
                    <Shimmer height={24} width="60%" />
                  </>
                ) : (
                  <>
                    <Text
                      variant="heading-lg"
                      className="overflow-wrap-anywhere"
                    >
                      {namaKs || '-'}
                    </Text>

                    {/* TODO: check if we can get the school name */}
                    <Text variant="body-lg" color="subdued">
                      |
                    </Text>

                    <div className="overflow-wrap-anywhere">
                      <Text variant="body-lg" color="subdued">
                        {satuanPendidikan || '-'}
                      </Text>
                    </div>
                  </>
                )}
              </div>

              <section>
                {periodYear >= 2025 ? (
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={assetUrl(
                        '/illustrations/img-badge-star-ribbon-pro.png'
                      )}
                      className="flex-shrink-0 flex-grow-0"
                      alt=""
                      width={52}
                      height={52}
                    />
                    <Text variant="heading-sm" as="h4">
                      Hasil Kerja Utama
                    </Text>
                  </div>
                ) : (
                  <Text as="h2" variant="heading-sm" className="mb-4">
                    Hasil Kerja Utama
                  </Text>
                )}

                <div className="flex flex-col gap-4">
                  {rencanaHasilKerja.map((item, index) => {
                    return (
                      <Fragment key={index}>
                        <PanelKinerja
                          hideDetails={hideRhkDetails}
                          kinerja={item}
                          number={index + 1}
                        />
                      </Fragment>
                    )
                  })}
                </div>
              </section>

              {periodYear >= 2025 && (
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={assetUrl('/illustrations/img-badge-star.png')}
                      className="flex-shrink-0 flex-grow-0"
                      alt=""
                      width={52}
                      height={52}
                    />
                    <Text variant="heading-sm">Praktik Kinerja</Text>
                  </div>

                  <div className="space-y-4">
                    {praktikKinerja.map((item, index) => {
                      return (
                        <Fragment key={index}>
                          <PanelKinerja
                            hideDetails={hideRhkDetails}
                            kinerja={item}
                            number={index + 1}
                          />
                        </Fragment>
                      )
                    })}
                  </div>
                </section>
              )}

              {periodYear >= 2025 && (
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={assetUrl('/illustrations/img-document.png')}
                      className="flex-shrink-0 flex-grow-0"
                      alt=""
                      width={52}
                      height={52}
                    />
                    <Text variant="heading-sm">Pengembangan Kompetensi</Text>
                  </div>
                  <div className="space-y-4">
                    {pengembanganKompetensi.map((item, index) => {
                      return (
                        <Fragment key={index}>
                          <PanelKinerja
                            hideDetails={hideRhkDetails}
                            kinerja={item}
                            number={index + 1}
                          />
                        </Fragment>
                      )
                    })}
                  </div>
                </section>
              )}

              <section className="mt-9">
                <Text as="h2" variant="heading-sm" className="mb-4">
                  Perilaku Kerja
                </Text>

                <TablePelaksanaanKinerja>
                  <TableHeader>
                    <TableHeaderItem>No.</TableHeaderItem>
                    <TableHeaderItem>Aspek</TableHeaderItem>
                    <TableHeaderItem>Indikator Perilaku</TableHeaderItem>
                    <TableHeaderItem>
                      Ekspektasi Khusus Pimpinan
                    </TableHeaderItem>
                  </TableHeader>
                  <TableBody>
                    {perilakuKerja?.map((item, index) => (
                      <TableRow key={`perilaku-kerja-${npsn}-${index}`}>
                        <TableRowItem>{item.no}</TableRowItem>
                        <TableRowItem>{item.aspek}</TableRowItem>
                        <TableRowItem>{item.indikator}</TableRowItem>
                        <TableRowItem>{item.ekspektasi}</TableRowItem>
                      </TableRow>
                    ))}
                  </TableBody>
                </TablePelaksanaanKinerja>
              </section>
            </div>

            <Separator />

            <div className="px-8 py-4 flex gap-5 justify-end">
              <Button
                size="lg"
                color="white"
                onClick={() => {
                  router.back()
                }}
              >
                Kembali
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
export default Perencanaan
